import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBack } from '../lib/back.js';
import type { Shift } from '@keetcheck/shared';
import { theme } from '../theme.js';
import { useAuth } from '../context/AuthContext.js';
import { useCoverRequests, useActivities, useFaults } from '../lib/queries.js';
import { OffRunList, RespondingStrip } from '../components/OffRun.js';
import { useOnNow, whoLabel } from '../lib/onnow.js';
import { PageShell } from '../components/PageShell.js';
import { Icon } from '../components/Icon.js';
import { CapabilityStrip, CoverStatusPill } from '../components/CoverBits.js';
import { Who } from './ShiftsPage.js';
import { KIND_LABEL, timeRange, rangeLabel, dayLabel, localDay } from '../lib/shifts.js';

/** Re-render every minute so "now" stays now. */
function useMinute() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 60_000); return () => clearInterval(t); }, []);
  return now;
}

/**
 * The UDO board: what's off run, who's on right now and what changes next,
 * cover gaps in the next 48 hours, and today's activities. Everyone can see it.
 */
export function NowPage() {
  const nav = useNavigate();
  const back = useBack('/');
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const now = useMinute();
  const { current, next, isLoading } = useOnNow(orgId, now);
  const { data: open = [] } = useCoverRequests(orgId, 'open');
  const soon = open.filter((r) => new Date(r.startsAt).getTime() < now.getTime() + 48 * 3600_000 && r.gaps.length > 0);
  const { data: acts = [] } = useActivities(orgId);
  const today = acts.filter((a) => !a.cancelled && localDay(a.startsAt) === localDay(now.toISOString()));
  const { data: faults } = useFaults(orgId);

  return (
    <PageShell>
      <div style={{ padding: '12px 16px', background: theme.surface, borderBottom: `1px solid ${theme.border}`, position: 'sticky', top: 0, zIndex: 5 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={back} aria-label="Back" style={iconBtn}><Icon name="back" size={18} /></button>
          <div style={{ fontSize: 20, fontWeight: 800, color: theme.text, flex: 1 }}>On now</div>
          <div style={{ fontSize: 13, color: theme.textMuted }}>{dayLabel(now.toISOString())} · {now.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}</div>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', background: theme.bg, padding: '12px 16px 32px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {isLoading && <div style={muted}>Loading…</div>}

        <RespondingStrip responding={faults?.responding} />
        <OffRunList assets={faults?.offRun ?? []} />

        <Section title="Right now">
          {!isLoading && current.length === 0 && <div style={muted}>Nobody is rostered at this moment.</div>}
          {current.map((s) => <Row key={s.id} s={s} />)}
        </Section>

        {next.length > 0 && (
          <Section title={`Next · ${dayLabel(next[0]!.startsAt)} ${timeRange(next[0]!.startsAt, next[0]!.endsAt)}`}>
            {next.map((s) => <Row key={s.id} s={s} compact />)}
          </Section>
        )}

        <Section title="Cover gaps · next 48 hours">
          {soon.length === 0 && <div style={muted}>No open cover requests.</div>}
          {soon.map((r) => (
            <button key={r.id} onClick={() => nav(`/cover/${r.id}`)} style={rowBtn}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <strong style={{ color: theme.text }}>{KIND_LABEL[r.kind]}</strong>
                  <CoverStatusPill status={r.status} partly={r.takes.length > 0} />
                </span>
                <span style={{ fontSize: 12, color: theme.textMuted }}>
                  {r.requester.displayName} · {rangeLabel(r.startsAt, r.endsAt)} · gap {r.gaps.map((g) => timeRange(g.startsAt, g.endsAt)).join(', ')}
                </span>
              </span>
              <Icon name="chevron" size={14} style={{ color: theme.textMuted }} />
            </button>
          ))}
        </Section>

        {today.length > 0 && (
          <Section title="Today">
            {today.map((a) => (
              <button key={a.id} onClick={() => nav(`/activity/${a.id}`)} style={rowBtn}>
                <span style={{ flex: 1 }}>
                  <strong style={{ color: theme.text }}>{a.title}</strong>
                  <span style={{ fontSize: 12, color: theme.textMuted }}> · {timeRange(a.startsAt, a.endsAt)}{a.hostTeam ? ` · ${a.hostTeam.name} hosting` : ''}</span>
                </span>
                <Icon name="chevron" size={14} style={{ color: theme.textMuted }} />
              </button>
            ))}
          </Section>
        )}

        <button onClick={() => nav('/shifts?tab=week')} style={{ ...rowBtn, justifyContent: 'center', color: theme.accent, fontWeight: 700 }}>
          The whole week →
        </button>
      </div>
    </PageShell>
  );
}

function Row({ s, compact = false }: { s: Shift; compact?: boolean }) {
  return (
    <div style={{ padding: '8px 0', borderTop: `1px solid ${theme.border}` }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
        <span style={{ width: 90, flexShrink: 0, fontSize: 13, fontWeight: 700, color: theme.text }}>{KIND_LABEL[s.kind]}</span>
        <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: theme.textDim }}>
          {compact ? whoLabel(s) : <Who shift={s} />}
        </span>
      </div>
      {!compact && s.kind === 'duty_crew' && s.capability && !s.crew && <div style={{ marginTop: 6 }}><CapabilityStrip cap={s.capability} /></div>}
      <div style={{ fontSize: 11, color: theme.textMuted, marginLeft: 98 }}>until {new Date(s.endsAt).toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' })}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ padding: '10px 14px 6px', borderRadius: 12, background: theme.surface, border: `1px solid ${theme.border}`, flexShrink: 0 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase', color: theme.textMuted, marginBottom: 4 }}>{title}</div>
      {children}
    </div>
  );
}

const muted: React.CSSProperties = { fontSize: 13, color: theme.textMuted, padding: '6px 0 8px' };
const rowBtn: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '9px 0', background: 'none', border: 'none',
  borderTop: `1px solid ${theme.border}`, textAlign: 'left', cursor: 'pointer', fontFamily: theme.font, fontSize: 13,
};

const iconBtn: React.CSSProperties = {
  width: 36, height: 36, border: `1px solid ${theme.border}`, background: theme.surface2, color: theme.text,
  borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0,
};
